/**
 * Configuration Queries
 *
 * React Query hooks for managing tab and page configuration data.
 * Includes mock data for CyberSweep Client Details tab.
 */

import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import {
  ApplicationPageConfig,
  TabConfiguration,
  TabSection,
  FormSectionConfig,
  TableSectionConfig,
  WidgetSectionConfig,
  ActivitySectionConfig,
} from '../../types/tabConfiguration';
import { persistedOMRecordPages, type RecordPage, type PageTab, type PageSection, type PageComponent } from './recordPageQueries';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { fetchPageLayoutTemplates } from '../gobodo/gobodoPageLayoutService';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
import { getRecordsForObject } from '../recordStorageService';
import { fetchClientCSAuditsViaTraversal } from './csAuditTraversalService';
import { hydrateCSAuditRecordsForListUi } from './csAuditHydration';

// ============================================
// QUERY KEYS
// ============================================

/**
 * Query key factory for configuration-related queries
 */
export const configKeys = {
  all: ['configuration'] as const,
  pages: () => [...configKeys.all, 'pages'] as const,
  page: (pageId: string) => [...configKeys.pages(), pageId] as const,
  tabs: (pageId: string) => [...configKeys.page(pageId), 'tabs'] as const,
  tab: (pageId: string, tabId: string) => [...configKeys.tabs(pageId), tabId] as const,
};

/**
 * Query key factory for data-related queries
 */
export const dataKeys = {
  all: ['data'] as const,
  records: (objectType: string) => [...dataKeys.all, objectType] as const,
  record: (objectType: string, recordId: string) => [...dataKeys.records(objectType), recordId] as const,
};

// ============================================
// MOCK DATA
// ============================================

// Field types and picklist values are now auto-resolved at render time by
// DynamicFormSection using src/services/sfFieldMetadataService.ts
// (generated from: npm run generate:sf-metadata)

/**
 * Mock application page configurations
 * Currently includes CyberSweep Client Detail page with multiple tabs
 */
const mockApplicationConfigs: Record<string, ApplicationPageConfig> = {
  cybersweep_client_detail: {
    id: 'cybersweep_client_detail',
    applicationName: 'cybersweep',
    pageName: 'Client Detail',
    pageType: 'detail',
    title: '',
    description: '',
    tabs: [
      // Tab 1: Account Details (full SF-style layout)
      {
        id: 'cs_account_detail',
        pageId: 'cybersweep_client_detail',
        label: 'Details',
        icon: 'User',
        order: 1,
        isDefault: true,
        lazyLoad: false,
        sections: [
          {
            id: 'cs_acct_info',
            type: 'form',
            name: 'account_information',
            label: 'Account Information',
            order: 1,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cs_a_name', fieldName: 'clientName', fieldLabel: 'Account Name', fieldType: 'text', required: true, readonly: true, width: 'full', column: 1, order: 1 },
                { id: 'cs_a_owner', fieldName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'lookup', lookupObject: 'User', required: false, readonly: false, width: 'full', column: 2, order: 2 },
                { id: 'cs_a_spouse', fieldName: 'spouse', fieldLabel: 'Spouse', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 3 },
                { id: 'cs_a_phone', fieldName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', required: false, readonly: false, width: 'full', column: 2, order: 4 },
                { id: 'cs_a_parent', fieldName: 'primaryParent', fieldLabel: 'Primary Parent', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 5 },
                { id: 'cs_a_mobile', fieldName: 'mobile', fieldLabel: 'Mobile', fieldType: 'phone', required: false, readonly: false, width: 'full', column: 2, order: 6 },
                { id: 'cs_a_dob', fieldName: 'dateOfBirth', fieldLabel: 'Birthday', fieldType: 'date', required: false, readonly: false, width: 'full', column: 1, order: 7 },
                { id: 'cs_a_bulksms', fieldName: 'bulkSmsStatus', fieldLabel: 'Bulk SMS Status', fieldType: 'select', required: false, readonly: false, width: 'full', column: 2, order: 8, options: [{ label: 'Active', value: 'Active' }, { label: 'Opted Out', value: 'Opted Out' }, { label: 'Never Subscribed', value: 'Never Subscribed' }] },
                { id: 'cs_a_ssn', fieldName: 'socialSecurityNumber', fieldLabel: 'SSN', fieldType: 'ssn', required: false, readonly: false, width: 'full', column: 1, order: 9, maxLength: 11, validation: { pattern: '^\\d{3}-\\d{2}-\\d{4}$', message: 'SSN must be in format 000-00-0000' }, masked: true },
                { id: 'cs_a_teamemail', fieldName: 'teamEmail', fieldLabel: 'Team Email', fieldType: 'email', required: false, readonly: false, width: 'full', column: 2, order: 10 },
                { id: 'cs_a_collections', fieldName: 'numberOfCollectionAccounts', fieldLabel: 'Number of Collection Accounts', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 11 },
                { id: 'cs_a_email', fieldName: 'email', fieldLabel: 'Email', fieldType: 'email', required: false, readonly: false, width: 'full', column: 2, order: 12 },
                { id: 'cs_a_networth', fieldName: 'estimatedNetworth', fieldLabel: 'Estimated Networth', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 13 },
                { id: 'cs_a_emailccs', fieldName: 'emailCCs', fieldLabel: "Email CC's", fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 14 },
                { id: 'cs_a_salary', fieldName: 'annualSalary', fieldLabel: 'Annual Salary', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 15 },
                { id: 'cs_a_regalteamemail', fieldName: 'regalTeamEmail', fieldLabel: 'Regal Team Email', fieldType: 'email', required: false, readonly: false, width: 'full', column: 2, order: 16 },
                { id: 'cs_a_creditreport', fieldName: 'creditReportFlag', fieldLabel: 'CreditReport', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 17 },
                { id: 'cs_a_bulkemail', fieldName: 'bulkEmailStatus', fieldLabel: 'Bulk Email Status', fieldType: 'select', required: false, readonly: false, width: 'full', column: 2, order: 18, options: [{ label: 'Single Opt-in', value: 'Single Opt-in' }, { label: 'Transactional Only', value: 'Transactional Only' }] },
                { id: 'cs_a_resstatus', fieldName: 'residentialStatus', fieldLabel: 'Residential Status', fieldType: 'select', required: false, readonly: false, width: 'full', column: 1, order: 19, options: [{ label: 'Own', value: 'Own' }, { label: 'Rent', value: 'Rent' }, { label: 'Other', value: 'Other' }] },
                { id: 'cs_a_referralsrcloc', fieldName: 'referralSourceLocation', fieldLabel: 'Referral Source Location', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 20 },
                { id: 'cs_a_resamt', fieldName: 'residentialAmount', fieldLabel: 'Residential Amount', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 21 },
                { id: 'cs_a_leadsrc', fieldName: 'leadSource', fieldLabel: 'Lead Source', fieldType: 'select', required: false, readonly: false, width: 'full', column: 2, order: 22, options: [{ label: 'Advertisement', value: 'Advertisement' }, { label: 'Customer Event', value: 'Customer Event' }, { label: 'Employee Referral', value: 'Employee Referral' }, { label: 'Google Adwords', value: 'Google Adwords' }, { label: 'Partner', value: 'Partner' }, { label: 'Purchased List', value: 'Purchased List' }, { label: 'Tradeshow', value: 'Tradeshow' }, { label: 'Webinar', value: 'Webinar' }, { label: 'Website', value: 'Website' }, { label: 'Social4M Event', value: 'Social4M Event' }, { label: 'Client Referral', value: 'Client Referral' }, { label: 'External Referral', value: 'External Referral' }, { label: 'Returning Client', value: 'Returning Client' }, { label: 'Other', value: 'Other' }] },
                { id: 'cs_a_empstatus', fieldName: 'employmentStatus', fieldLabel: 'Employment Status', fieldType: 'select', required: false, readonly: false, width: 'full', column: 1, order: 23, options: [{ label: 'Employed', value: 'Employed' }, { label: 'Self-Employed', value: 'Self-Employed' }, { label: 'Unemployed', value: 'Unemployed' }, { label: 'Retired', value: 'Retired' }, { label: 'Student', value: 'Student' }, { label: 'Other', value: 'Other' }] },
                { id: 'cs_a_referralco', fieldName: 'referralCompany', fieldLabel: 'Referral Company', fieldType: 'lookup', lookupObject: 'Account', required: false, readonly: false, width: 'full', column: 2, order: 24 },
                { id: 'cs_a_employer', fieldName: 'employer', fieldLabel: 'Employer', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 25 },
                { id: 'cs_a_referralind', fieldName: 'referralCompanyIndustry', fieldLabel: 'Referral Company Industry', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 26 },
                { id: 'cs_a_datasource', fieldName: 'dataSource', fieldLabel: 'DataSource', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 27 },
                { id: 'cs_a_referralpartner', fieldName: 'referralPartnerContact', fieldLabel: 'Referral Partner Contact', fieldType: 'lookup', lookupObject: 'Contact', required: false, readonly: false, width: 'full', column: 2, order: 28 },
                { id: 'cs_a_relopp', fieldName: 'relatedInstitutionalOpportunity', fieldLabel: 'Related Institutional Opportunity', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 29 },
                { id: 'cs_a_referralperson', fieldName: 'referralPersonContact', fieldLabel: 'Referral Person Contact', fieldType: 'lookup', lookupObject: 'Account', required: false, readonly: false, width: 'full', column: 2, order: 30 },
                { id: 'cs_a_arraykey', fieldName: 'arrayClientKey', fieldLabel: 'ArrayClientKey', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 31 },
                { id: 'cs_a_arraymissing', fieldName: 'arrayRegistrationMissingData', fieldLabel: 'Array Registration Missing Data', fieldType: 'text', required: false, readonly: true, width: 'full', column: 2, order: 32 },
                { id: 'cs_a_arrayfail', fieldName: 'arrayRegistrationFailures', fieldLabel: 'Array Registration Failures', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 33 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
          {
            id: 'cs_billing_address',
            type: 'form',
            name: 'billing_address',
            label: 'Billing Address',
            order: 2,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cs_ba_street', fieldName: 'billingStreet', fieldLabel: 'Street', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 1 },
                { id: 'cs_ba_city', fieldName: 'billingCity', fieldLabel: 'City', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 2 },
                { id: 'cs_ba_state', fieldName: 'billingState', fieldLabel: 'State', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 3 },
                { id: 'cs_ba_zip', fieldName: 'billingPostalCode', fieldLabel: 'Zip Code', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 4 },
                { id: 'cs_ba_country', fieldName: 'billingCountry', fieldLabel: 'Country', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 5 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
          {
            id: 'cs_service_mgmt',
            type: 'form',
            name: 'account_management',
            label: 'Account Management',
            order: 3,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cs_sm_self', fieldName: 'serviceManagement', fieldLabel: 'Service Management', fieldType: 'select', required: false, readonly: false, width: 'full', column: 1, order: 1, options: [{ label: 'Self', value: 'Self' }, { label: 'Account Manager Managed', value: 'Account Manager Managed' }] },
                { id: 'cs_sm_acctmgrco', fieldName: 'accountManagerCompany', fieldLabel: 'Account Manager Company', fieldType: 'lookup', lookupObject: 'Account', required: false, readonly: false, width: 'full', column: 2, order: 2 },
                { id: 'cs_sm_acctmgr', fieldName: 'accountManager', fieldLabel: 'Account Manager', fieldType: 'lookup', lookupObject: 'Contact', required: false, readonly: false, width: 'full', column: 1, order: 3 },
                { id: 'cs_sm_daytodaycontact', fieldName: 'dayToDayContact', fieldLabel: 'Day-to-Day Contact', fieldType: 'lookup', lookupObject: 'Contact', required: false, readonly: false, width: 'full', column: 2, order: 4 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
          {
            id: 'cs_system_info',
            type: 'form',
            name: 'system_information',
            label: 'System Information',
            order: 4,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cs_si_createdby', fieldName: 'recordCreatedBy', fieldLabel: 'Created By', fieldType: 'lookup', lookupObject: 'User', required: false, readonly: true, width: 'full', column: 1, order: 1 },
                { id: 'cs_si_modifiedby', fieldName: 'lastModifiedBy', fieldLabel: 'Last Modified By', fieldType: 'lookup', lookupObject: 'User', required: false, readonly: true, width: 'full', column: 2, order: 2 },
                { id: 'cs_si_createddate', fieldName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', required: false, readonly: true, width: 'full', column: 1, order: 3 },
                { id: 'cs_si_modifieddate', fieldName: 'lastModifiedDate', fieldLabel: 'Last Modified Date', fieldType: 'date', required: false, readonly: true, width: 'full', column: 2, order: 4 },
                { id: 'cs_si_legacyid', fieldName: 'legacyId', fieldLabel: 'LegacyId', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 5 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
        ],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 2: Subscription (CyberSweep-specific fields)
      {
        id: 'cs_subscription_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Subscription',
        icon: 'Gift',
        order: 2,
        isDefault: false,
        lazyLoad: false,
        sections: [
          {
            id: 'cs_sub_details',
            type: 'form',
            name: 'subscription_details',
            label: 'Details',
            order: 1,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cs_s_start', fieldName: 'cyberSweepStartDate', fieldLabel: 'Cyber Sweep Start Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 1, order: 1 },
                { id: 'cs_s_recipients', fieldName: 'csReportsRecipients', fieldLabel: 'CS Reports Recipients', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 2 },
                { id: 'cs_s_end', fieldName: 'cyberSweepEndDate', fieldLabel: 'Cyber Sweep End Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 1, order: 3 },
                { id: 'cs_s_initreport', fieldName: 'initialReport', fieldLabel: 'Initial Report', fieldType: 'checkbox', required: false, readonly: false, width: 'full', column: 2, order: 4 },
                { id: 'cs_s_stage', fieldName: 'cyberSweepStage', fieldLabel: 'Cyber Sweep Stage', fieldType: 'select', required: false, readonly: false, width: 'full', column: 1, order: 5, options: [{ label: 'Current Client', value: 'Current Client' }, { label: 'Cancelled', value: 'Cancelled' }, { label: 'Complete', value: 'Complete' }] },
                { id: 'cs_s_initdate', fieldName: 'initialReportDate', fieldLabel: 'Initial Report Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 2, order: 6 },
                { id: 'cs_s_status', fieldName: 'cyberSweepStatus', fieldLabel: 'Cyber Sweep Status', fieldType: 'select', required: false, readonly: false, width: 'full', column: 1, order: 7, options: [{ label: 'Pending Enrollments', value: 'Pending Enrollments' }, { label: 'Lacking Requirements', value: 'Lacking Requirements' }, { label: 'Awaiting Assignment', value: 'Awaiting Assignment' }, { label: 'Pending Checklists', value: 'Pending Checklists' }, { label: 'Awaiting Initial Report', value: 'Awaiting Initial Report' }, { label: 'Awaiting Final Report', value: 'Awaiting Final Report' }, { label: 'Quarterly Report 1', value: 'Quarterly Report 1' }, { label: 'Quarterly Report 2', value: 'Quarterly Report 2' }, { label: 'Quarterly Report 3', value: 'Quarterly Report 3' }, { label: 'Quarterly Report 4', value: 'Quarterly Report 4' }] },
                { id: 'cs_s_finalreport', fieldName: 'finalReport', fieldLabel: 'Final Report', fieldType: 'checkbox', required: false, readonly: false, width: 'full', column: 2, order: 8 },
                { id: 'cs_s_phone', fieldName: 'clientPersonalPhone', fieldLabel: "Client's Personal Phone", fieldType: 'phone', required: false, readonly: false, width: 'full', column: 1, order: 9 },
                { id: 'cs_s_finaldate', fieldName: 'finalReportDate', fieldLabel: 'Final Report Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 2, order: 10 },
                { id: 'cs_s_pemail', fieldName: 'personalEmailAddress', fieldLabel: 'Personal Email Address', fieldType: 'email', required: false, readonly: false, width: 'full', column: 1, order: 11 },
                { id: 'cs_s_q1', fieldName: 'quarterlySweep1', fieldLabel: 'Quarterly Sweep 1', fieldType: 'checkbox', required: false, readonly: false, width: 'full', column: 2, order: 12 },
                { id: 'cs_s_curaddr', fieldName: 'currentHomeAddress', fieldLabel: 'Current Home Address', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 13 },
                { id: 'cs_s_s1date', fieldName: 'sweep1Date', fieldLabel: 'Sweep 1 Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 2, order: 14 },
                { id: 'cs_s_curcity', fieldName: 'currentCity', fieldLabel: 'Current City', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 15 },
                { id: 'cs_s_q2', fieldName: 'quarterlySweep2', fieldLabel: 'Quarterly Sweep 2', fieldType: 'checkbox', required: false, readonly: false, width: 'full', column: 2, order: 16 },
                { id: 'cs_s_curstate', fieldName: 'currentState', fieldLabel: 'Current State', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 17 },
                { id: 'cs_s_s2date', fieldName: 'sweep2Date', fieldLabel: 'Sweep 2 Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 2, order: 18 },
                { id: 'cs_s_curzip', fieldName: 'currentZipPostal', fieldLabel: 'Current Zip/Postal', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 19 },
                { id: 'cs_s_q3', fieldName: 'quarterlySweep3', fieldLabel: 'Quarterly Sweep 3', fieldType: 'checkbox', required: false, readonly: false, width: 'full', column: 2, order: 20 },
                { id: 'cs_s_prevaddr', fieldName: 'previousHomeAddress', fieldLabel: 'Previous Home Address', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 21 },
                { id: 'cs_s_s3date', fieldName: 'sweep3Date', fieldLabel: 'Sweep 3 Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 2, order: 22 },
                { id: 'cs_s_prevcity', fieldName: 'previousCity', fieldLabel: 'Previous City', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 23 },
                { id: 'cs_s_q4', fieldName: 'quarterlySweep4', fieldLabel: 'Quarterly Sweep 4', fieldType: 'checkbox', required: false, readonly: false, width: 'full', column: 2, order: 24 },
                { id: 'cs_s_prevstate', fieldName: 'previousState', fieldLabel: 'Previous State', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 25 },
                { id: 'cs_s_s4date', fieldName: 'sweep4Date', fieldLabel: 'Sweep 4 Date', fieldType: 'date', required: false, readonly: false, width: 'full', column: 2, order: 26 },
                { id: 'cs_s_prevzip', fieldName: 'previousZipPostal', fieldLabel: 'Previous Zip/Postal', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 27 },
                { id: 'cs_s_profession', fieldName: 'profession', fieldLabel: 'Profession', fieldType: 'multiselect', required: false, readonly: false, width: 'full', column: 2, order: 28, options: [{ label: 'Actor', value: 'Actor' }, { label: 'Artist', value: 'Artist' }, { label: 'Assistant', value: 'Assistant' }, { label: 'Athlete', value: 'Athlete' }, { label: 'Attorney', value: 'Attorney' }, { label: 'Baseball Player', value: 'Baseball Player' }, { label: 'Basketball Player', value: 'Basketball Player' }, { label: 'CEO', value: 'CEO' }, { label: 'Comedian', value: 'Comedian' }, { label: 'Correspondent', value: 'Correspondent' }, { label: 'CPA', value: 'CPA' }, { label: 'Designer', value: 'Designer' }, { label: 'Doctor', value: 'Doctor' }, { label: 'Film Director', value: 'Film Director' }, { label: 'Football Player', value: 'Football Player' }, { label: 'Heiress', value: 'Heiress' }, { label: 'Host', value: 'Host' }, { label: 'Internet Personality', value: 'Internet Personality' }, { label: 'Manager', value: 'Manager' }, { label: 'Model', value: 'Model' }, { label: 'Musician', value: 'Musician' }, { label: 'Producer', value: 'Producer' }, { label: 'Rapper', value: 'Rapper' }, { label: 'Singer', value: 'Singer' }, { label: 'Sportscaster', value: 'Sportscaster' }, { label: 'Writer', value: 'Writer' }, { label: 'Other', value: 'Other' }] },
                { id: 'cs_s_othprof', fieldName: 'otherProfession', fieldLabel: 'Other Profession', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 29, conditionalOn: 'profession', conditionalValue: 'Other' },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
        ],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 3: Classic Audit (moved here — after Subscription)
      {
        id: 'classic_audit_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Classic Audit',
        icon: 'FileText',
        order: 3,
        isDefault: false,
        lazyLoad: true,
        sections: [
          // Gmail Account Widget Section
          {
            id: 'gmail_account_widget_section',
            type: 'widget',
            name: 'gmail_account_widget',
            label: 'Gmail Account',
            order: 1,
            collapsible: false,
            collapsed: false,
            columns: 1,
            description: '',
            config: {
              componentName: 'GmailAccountWidget',
              props: {
                showStatus: true,
                showLastSync: true
              },
              refreshInterval: 60000
            } as WidgetSectionConfig
          },
          // CS Audits Table Section
          {
            id: 'cs_audits_table_section',
            type: 'table',
            name: 'cs_audits_table',
            label: 'CS Audits',
            order: 2,
            collapsible: true,
            collapsed: false,
            columns: 1,
            description: '',
            config: {
              dataSource: {
                type: 'query',
                query: {
                  objectType: 'cs_audits',
                  fields: ['auditId', 'auditType', 'auditor', 'dueDate', 'phaseStatus']
                }
              },
              columns: [
                { id: 'audit_id', field: 'auditId', label: 'AUDIT ID', sortable: true, width: '15%', align: 'left', dataType: 'text' },
                { id: 'audit_type', field: 'auditType', label: 'AUDIT TYPE', sortable: true, width: '20%', align: 'left', dataType: 'text' },
                { id: 'auditor', field: 'auditor', label: 'AUDITOR', sortable: true, width: '20%', align: 'left', dataType: 'text' },
                { id: 'due_date', field: 'dueDate', label: 'DUE DATE', sortable: true, width: '15%', align: 'left', dataType: 'date' },
                { id: 'phase_status', field: 'phaseStatus', label: 'PHASE STATUS', sortable: true, width: '20%', align: 'left', dataType: 'text' }
              ],
              actions: [
                { id: 'view_details', label: 'View Details', icon: 'Eye', type: 'inline', action: 'viewAuditDetails' }
              ],
              pagination: { enabled: true, pageSize: 10, pageSizeOptions: [10, 25, 50] },
              defaultSort: { field: 'dueDate', direction: 'desc' },
              searchable: true,
              searchPlaceholder: 'Search audits...'
            } as TableSectionConfig
          }
        ],
        visibility: { roles: ['admin', 'user', 'manager', 'auditor'] },
        permissions: { canView: ['admin', 'user', 'manager', 'auditor'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      },
      // Tab 4: Automated Audit (CSIP Information + related lists)
      {
        id: 'automated_audit_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Automated Audit',
        icon: 'Scan',
        order: 4,
        isDefault: false,
        lazyLoad: true,
        sections: [],
        visibility: { roles: ['admin', 'user', 'manager', 'auditor'] },
        permissions: { canView: ['admin', 'user', 'manager', 'auditor'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      },
      // Tab 5: Configuration (PII monitoring categories)
      {
        id: 'configuration_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Configuration',
        icon: 'Settings',
        order: 5,
        isDefault: false,
        lazyLoad: true,
        sections: [],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      },
      // Tab 6: Client Details (existing — renumbered)
      {
        id: 'client_details_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Client Details',
        icon: 'User',
        order: 4,
        isDefault: false,
        lazyLoad: false,
        sections: [
          // Basic Information Section
          {
            id: 'basic_information_section',
            type: 'form',
            name: 'basic_information',
            label: 'Basic Information',
            order: 1,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                {
                  id: 'field_client_name',
                  fieldName: 'clientName',
                  fieldLabel: 'Client Name',
                  fieldType: 'text',
                  placeholder: 'Enter client name',
                  required: true,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 1,
                  validation: [
                    {
                      type: 'required',
                      message: 'Client name is required'
                    },
                    {
                      type: 'minLength',
                      value: 2,
                      message: 'Client name must be at least 2 characters'
                    }
                  ]
                },
                {
                  id: 'field_client_number',
                  fieldName: 'clientNumber',
                  fieldLabel: 'Client Number',
                  fieldType: 'text',
                  placeholder: 'Auto-generated',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 1,
                  order: 2
                },
                {
                  id: 'field_email',
                  fieldName: 'email',
                  fieldLabel: 'Email Address',
                  fieldType: 'email',
                  placeholder: 'client@example.com',
                  required: true,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 3,
                  validation: [
                    {
                      type: 'required',
                      message: 'Email is required'
                    },
                    {
                      type: 'pattern',
                      value: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
                      message: 'Please enter a valid email address'
                    }
                  ]
                },
                {
                  id: 'field_phone',
                  fieldName: 'phone',
                  fieldLabel: 'Phone Number',
                  fieldType: 'phone',
                  placeholder: '(555) 123-4567',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 4
                },
                {
                  id: 'field_mobile',
                  fieldName: 'mobile',
                  fieldLabel: 'Mobile Number',
                  fieldType: 'phone',
                  placeholder: '(555) 987-6543',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 5
                },
                {
                  id: 'field_status',
                  fieldName: 'status',
                  fieldLabel: 'Client Status',
                  fieldType: 'select',
                  required: true,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 6,
                  options: [
                    { label: 'Active', value: 'active' },
                    { label: 'Inactive', value: 'inactive' },
                    { label: 'Pending', value: 'pending' },
                    { label: 'Suspended', value: 'suspended' }
                  ],
                  defaultValue: 'active'
                },
                {
                  id: 'field_date_joined',
                  fieldName: 'dateJoined',
                  fieldLabel: 'Date Joined',
                  fieldType: 'date',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 7
                },
                {
                  id: 'field_notes',
                  fieldName: 'notes',
                  fieldLabel: 'Notes',
                  fieldType: 'textarea',
                  placeholder: 'Additional client notes...',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 8
                }
              ],
              layout: 'horizontal',
              showLabels: true
            } as FormSectionConfig
          },
          // Reports & Profession Section
          {
            id: 'reports_profession_section',
            type: 'form',
            name: 'reports_profession',
            label: 'Reports & Profession',
            order: 2,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: 'Credit reporting and professional information',
            config: {
              fields: [
                {
                  id: 'field_credit_score',
                  fieldName: 'creditScore',
                  fieldLabel: 'Credit Score',
                  fieldType: 'number',
                  placeholder: '300-850',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 1,
                  validation: [
                    {
                      type: 'custom',
                      message: 'Credit score must be between 300 and 850',
                      validator: (value: any) => {
                        const num = parseInt(value);
                        return num >= 300 && num <= 850;
                      }
                    }
                  ]
                },
                {
                  id: 'field_last_report_date',
                  fieldName: 'lastReportDate',
                  fieldLabel: 'Last Report Date',
                  fieldType: 'date',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 1,
                  order: 2
                },
                {
                  id: 'field_profession',
                  fieldName: 'profession',
                  fieldLabel: 'Profession',
                  fieldType: 'text',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 3
                },
                {
                  id: 'field_employer',
                  fieldName: 'employer',
                  fieldLabel: 'Employer',
                  fieldType: 'text',
                  placeholder: 'Enter employer name',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 4
                }
              ],
              layout: 'horizontal',
              showLabels: true
            } as FormSectionConfig
          }
        ],
        visibility: {
          roles: ['admin', 'user', 'manager']
        },
        permissions: {
          canView: ['admin', 'user', 'manager'],
          canEdit: ['admin', 'manager'],
          canDelete: ['admin']
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      },
      // Billing Information Tab
      {
        id: 'billing_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Billing',
        icon: 'CreditCard',
        order: 5,
        isDefault: false,
        lazyLoad: true,
        sections: [
          {
            id: 'billing_details_section',
            type: 'form',
            name: 'billing_details',
            label: 'Billing Details',
            order: 1,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: 'Client billing and payment information',
            config: {
              fields: [
                {
                  id: 'field_billing_email',
                  fieldName: 'billingEmail',
                  fieldLabel: 'Billing Email',
                  fieldType: 'email',
                  placeholder: 'billing@example.com',
                  required: true,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 1,
                  validation: [
                    {
                      type: 'required',
                      message: 'Billing email is required'
                    },
                    {
                      type: 'pattern',
                      value: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
                      message: 'Please enter a valid email address'
                    }
                  ]
                },
                {
                  id: 'field_billing_cycle',
                  fieldName: 'billingCycle',
                  fieldLabel: 'Billing Cycle',
                  fieldType: 'select',
                  required: true,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 2,
                  options: [
                    { label: 'Monthly', value: 'monthly' },
                    { label: 'Quarterly', value: 'quarterly' },
                    { label: 'Semi-Annual', value: 'semi-annual' },
                    { label: 'Annual', value: 'annual' }
                  ],
                  defaultValue: 'monthly'
                },
                {
                  id: 'field_payment_method',
                  fieldName: 'paymentMethod',
                  fieldLabel: 'Payment Method',
                  fieldType: 'select',
                  required: true,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 3,
                  options: [
                    { label: 'Credit Card', value: 'credit_card' },
                    { label: 'ACH/Bank Transfer', value: 'ach' },
                    { label: 'Check', value: 'check' },
                    { label: 'Wire Transfer', value: 'wire' }
                  ],
                  defaultValue: 'credit_card'
                },
                {
                  id: 'field_next_billing_date',
                  fieldName: 'nextBillingDate',
                  fieldLabel: 'Next Billing Date',
                  fieldType: 'date',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 2,
                  order: 4
                },
                {
                  id: 'field_monthly_amount',
                  fieldName: 'monthlyAmount',
                  fieldLabel: 'Monthly Amount',
                  fieldType: 'currency',
                  placeholder: '$0.00',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 5
                },
                {
                  id: 'field_account_balance',
                  fieldName: 'accountBalance',
                  fieldLabel: 'Account Balance',
                  fieldType: 'currency',
                  placeholder: '$0.00',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 2,
                  order: 6
                }
              ],
              layout: 'horizontal',
              showLabels: true
            } as FormSectionConfig
          }
        ],
        visibility: {
          roles: ['admin', 'manager', 'billing']
        },
        permissions: {
          canView: ['admin', 'manager', 'billing'],
          canEdit: ['admin', 'billing'],
          canDelete: ['admin']
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      },
      // Activity History Tab
      {
        id: 'activity_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Activity',
        icon: 'Activity',
        order: 6,
        isDefault: false,
        lazyLoad: true,
        sections: [
          {
            id: 'recent_activity_section',
            type: 'form',
            name: 'recent_activity',
            label: 'Recent Activity',
            order: 1,
            collapsible: false,
            collapsed: false,
            columns: 1,
            description: 'Timeline of recent client interactions and system events',
            config: {
              fields: [
                {
                  id: 'field_last_login',
                  fieldName: 'lastLogin',
                  fieldLabel: 'Last Login',
                  fieldType: 'datetime',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 1,
                  order: 1
                },
                {
                  id: 'field_last_contact',
                  fieldName: 'lastContact',
                  fieldLabel: 'Last Contact Date',
                  fieldType: 'datetime',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 1,
                  order: 2
                },
                {
                  id: 'field_contact_method',
                  fieldName: 'lastContactMethod',
                  fieldLabel: 'Last Contact Method',
                  fieldType: 'select',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 1,
                  order: 3,
                  options: [
                    { label: 'Email', value: 'email' },
                    { label: 'Phone', value: 'phone' },
                    { label: 'In-Person', value: 'in_person' },
                    { label: 'Portal', value: 'portal' }
                  ]
                },
                {
                  id: 'field_activity_notes',
                  fieldName: 'activityNotes',
                  fieldLabel: 'Activity Notes',
                  fieldType: 'textarea',
                  placeholder: 'Recent activity summary...',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 1,
                  order: 4
                }
              ],
              layout: 'vertical',
              showLabels: true
            } as FormSectionConfig
          }
        ],
        visibility: {
          roles: ['admin', 'user', 'manager']
        },
        permissions: {
          canView: ['admin', 'user', 'manager'],
          canEdit: ['admin', 'manager'],
          canDelete: ['admin']
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      },
      // Documents Tab
      {
        id: 'documents_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Documents',
        icon: 'FileText',
        order: 8,
        isDefault: false,
        lazyLoad: true,
        sections: [
          {
            id: 'document_management_section',
            type: 'form',
            name: 'document_management',
            label: 'Document Management',
            order: 1,
            collapsible: false,
            collapsed: false,
            columns: 2,
            description: 'Client document storage and management',
            config: {
              fields: [
                {
                  id: 'field_id_verification',
                  fieldName: 'idVerificationStatus',
                  fieldLabel: 'ID Verification Status',
                  fieldType: 'select',
                  required: true,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 1,
                  options: [
                    { label: 'Verified', value: 'verified' },
                    { label: 'Pending', value: 'pending' },
                    { label: 'Not Submitted', value: 'not_submitted' },
                    { label: 'Rejected', value: 'rejected' }
                  ],
                  defaultValue: 'not_submitted'
                },
                {
                  id: 'field_id_expiry',
                  fieldName: 'idExpiryDate',
                  fieldLabel: 'ID Expiry Date',
                  fieldType: 'date',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 2
                },
                {
                  id: 'field_contract_signed',
                  fieldName: 'contractSigned',
                  fieldLabel: 'Contract Signed',
                  fieldType: 'checkbox',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 3,
                  defaultValue: false
                },
                {
                  id: 'field_contract_date',
                  fieldName: 'contractSignedDate',
                  fieldLabel: 'Contract Signed Date',
                  fieldType: 'date',
                  required: false,
                  readonly: true,
                  width: 'full',
                  column: 2,
                  order: 4
                },
                {
                  id: 'field_document_notes',
                  fieldName: 'documentNotes',
                  fieldLabel: 'Document Notes',
                  fieldType: 'textarea',
                  placeholder: 'Notes about client documents...',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 5
                }
              ],
              layout: 'horizontal',
              showLabels: true
            } as FormSectionConfig
          }
        ],
        visibility: {
          roles: ['admin', 'user', 'manager']
        },
        permissions: {
          canView: ['admin', 'user', 'manager'],
          canEdit: ['admin', 'manager'],
          canDelete: ['admin']
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      },
      // Notes Tab
      {
        id: 'notes_tab',
        pageId: 'cybersweep_client_detail',
        label: 'Notes',
        icon: 'StickyNote',
        order: 9,
        isDefault: false,
        lazyLoad: true,
        sections: [
          {
            id: 'client_notes_section',
            type: 'form',
            name: 'client_notes',
            label: 'Client Notes',
            order: 1,
            collapsible: false,
            collapsed: false,
            columns: 1,
            description: 'Internal notes and communications about the client',
            config: {
              fields: [
                {
                  id: 'field_internal_notes',
                  fieldName: 'internalNotes',
                  fieldLabel: 'Internal Notes',
                  fieldType: 'textarea',
                  placeholder: 'Add internal notes visible only to staff...',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 1
                },
                {
                  id: 'field_client_preferences',
                  fieldName: 'clientPreferences',
                  fieldLabel: 'Client Preferences',
                  fieldType: 'textarea',
                  placeholder: 'Communication preferences, special requirements, etc...',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 2
                },
                {
                  id: 'field_alerts',
                  fieldName: 'alerts',
                  fieldLabel: 'Important Alerts',
                  fieldType: 'textarea',
                  placeholder: 'Any important alerts or warnings...',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 3
                },
                {
                  id: 'field_follow_up',
                  fieldName: 'followUpDate',
                  fieldLabel: 'Follow-up Date',
                  fieldType: 'date',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 4
                },
                {
                  id: 'field_follow_up_reason',
                  fieldName: 'followUpReason',
                  fieldLabel: 'Follow-up Reason',
                  fieldType: 'text',
                  placeholder: 'Reason for follow-up...',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 5
                }
              ],
              layout: 'vertical',
              showLabels: true
            } as FormSectionConfig
          }
        ],
        visibility: {
          roles: ['admin', 'user', 'manager']
        },
        permissions: {
          canView: ['admin', 'user', 'manager'],
          canEdit: ['admin', 'user', 'manager'],
          canDelete: ['admin']
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system'
      }
    ],
    defaultTabId: 'cs_account_detail',
    layout: 'standard',
    showHeader: true,
    permissions: {
      canView: ['admin', 'user', 'manager'],
      canEdit: ['admin', 'manager'],
      canDelete: ['admin']
    },
    isActive: true,
    isPublished: true,
    version: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'system',
    updatedBy: 'system'
  },
  credit_block_client_detail: {
    id: 'credit_block_client_detail',
    applicationName: 'credit_block',
    pageName: 'Client Detail',
    pageType: 'detail',
    title: '',
    description: '',
    tabs: [
      // Tab 1: Account Details (full SF Account layout — rendered by DynamicFormSection)
      {
        id: 'cb_account_detail',
        pageId: 'credit_block_client_detail',
        label: 'Details',
        icon: 'User',
        order: 1,
        isDefault: true,
        lazyLoad: false,
        sections: [
          {
            id: 'cb_client_managers',
            type: 'form',
            name: 'client_managers',
            label: 'Client Managers',
            order: 0,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: 'Client ownership and manager assignments',
            config: {
              fields: [
                { id: 'cb_cm_owner', fieldName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 1 },
                { id: 'cb_cm_company', fieldName: 'accountManagerCompany', fieldLabel: 'Account Manager Company', fieldType: 'lookup', lookupObject: 'Account', required: false, readonly: true, width: 'full', column: 2, order: 2 },
                { id: 'cb_cm_manager', fieldName: 'accountManager', fieldLabel: 'Account Manager', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 3 },
                { id: 'cb_cm_daytoday', fieldName: 'dayToDayContact', fieldLabel: 'Day-to-Day Contact', fieldType: 'text', required: false, readonly: true, width: 'full', column: 2, order: 4 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
          {
            id: 'cb_acct_info',
            type: 'form',
            name: 'account_information',
            label: 'Account Information',
            order: 1,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cb_a_name', fieldName: 'accountName', fieldLabel: 'Account Name', fieldType: 'text', required: true, readonly: true, width: 'full', column: 1, order: 1 },
                { id: 'cb_a_owner', fieldName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', required: false, readonly: true, width: 'full', column: 2, order: 2 },
                { id: 'cb_a_spouse', fieldName: 'spouse', fieldLabel: 'Spouse', fieldType: 'lookup', lookupObject: 'Client__c', required: false, readonly: false, width: 'full', column: 1, order: 3 },
                { id: 'cb_a_phone', fieldName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', required: false, readonly: false, width: 'full', column: 2, order: 4 },
                { id: 'cb_a_parent', fieldName: 'primaryParent', fieldLabel: 'Primary Parent', fieldType: 'lookup', lookupObject: 'Client__c', required: false, readonly: false, width: 'full', column: 1, order: 5 },
                { id: 'cb_a_mobile', fieldName: 'mobile', fieldLabel: 'Mobile', fieldType: 'phone', required: false, readonly: false, width: 'full', column: 2, order: 6 },
                { id: 'cb_a_dob', fieldName: 'dateOfBirth', fieldLabel: 'Birthday', fieldType: 'date', required: false, readonly: false, width: 'full', column: 1, order: 7 },
                { id: 'cb_a_bulksms', fieldName: 'bulkSmsStatus', fieldLabel: 'Bulk SMS Status', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 8 },
                { id: 'cb_a_ssn', fieldName: 'socialSecurityNumber', fieldLabel: 'SSN', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 9 },
                { id: 'cb_a_teamemail', fieldName: 'teamEmail', fieldLabel: 'Team Email', fieldType: 'email', required: false, readonly: false, width: 'full', column: 2, order: 10 },
                { id: 'cb_a_collections', fieldName: 'numberOfCollectionAccounts', fieldLabel: 'Number of Collection Accounts', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 11 },
                { id: 'cb_a_email', fieldName: 'email', fieldLabel: 'Email', fieldType: 'email', required: false, readonly: false, width: 'full', column: 2, order: 12 },
                { id: 'cb_a_networth', fieldName: 'estimatedNetworth', fieldLabel: 'Estimated Networth', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 13 },
                { id: 'cb_a_emailccs', fieldName: 'emailCCs', fieldLabel: "Email CC's", fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 14 },
                { id: 'cb_a_salary', fieldName: 'annualSalary', fieldLabel: 'Annual Salary', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 15 },
                { id: 'cb_a_regalteamemail', fieldName: 'regalTeamEmail', fieldLabel: 'Regal Team Email', fieldType: 'email', required: false, readonly: false, width: 'full', column: 2, order: 16 },
                { id: 'cb_a_creditreport', fieldName: 'creditReportFlag', fieldLabel: 'CreditReport', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 17 },
                { id: 'cb_a_bulkemail', fieldName: 'bulkEmailStatus', fieldLabel: 'Bulk Email Status', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 18 },
                { id: 'cb_a_resstatus', fieldName: 'residentialStatus', fieldLabel: 'Residential Status', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 19 },
                { id: 'cb_a_referralsrcloc', fieldName: 'referralSourceLocation', fieldLabel: 'Referral Source Location', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 20 },
                { id: 'cb_a_resamt', fieldName: 'residentialAmount', fieldLabel: 'Residential Amount', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 21 },
                { id: 'cb_a_leadsrc', fieldName: 'leadSource', fieldLabel: 'Lead Source', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 22 },
                { id: 'cb_a_empstatus', fieldName: 'employmentStatus', fieldLabel: 'Employment Status', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 23 },
                { id: 'cb_a_referralco', fieldName: 'referralCompany', fieldLabel: 'Referral Company', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 24 },
                { id: 'cb_a_employer', fieldName: 'employer', fieldLabel: 'Employer', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 25 },
                { id: 'cb_a_referralind', fieldName: 'referralCompanyIndustry', fieldLabel: 'Referral Company Industry', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 26 },
                { id: 'cb_a_datasource', fieldName: 'dataSource', fieldLabel: 'DataSource', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 27 },
                { id: 'cb_a_referralpartner', fieldName: 'referralPartnerContact', fieldLabel: 'Referral Partner Contact', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 28 },
                { id: 'cb_a_relopp', fieldName: 'relatedInstitutionalOpportunity', fieldLabel: 'Related Institutional Opportunity', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 29 },
                { id: 'cb_a_referralperson', fieldName: 'referralPersonContact', fieldLabel: 'Referral Person Contact', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 30 },
                { id: 'cb_a_arraykey', fieldName: 'arrayClientKey', fieldLabel: 'ArrayClientKey', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 31 },
                { id: 'cb_a_arraymissing', fieldName: 'arrayRegistrationMissingData', fieldLabel: 'Array Registration Missing Data', fieldType: 'text', required: false, readonly: true, width: 'full', column: 2, order: 32 },
                { id: 'cb_a_arrayfail', fieldName: 'arrayRegistrationFailures', fieldLabel: 'Array Registration Failures', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 33 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
          {
            id: 'cb_billing_address',
            type: 'form',
            name: 'billing_address',
            label: 'Billing Address',
            order: 2,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cb_ba_street', fieldName: 'billingStreet', fieldLabel: 'Street', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 1 },
                { id: 'cb_ba_city', fieldName: 'billingCity', fieldLabel: 'City', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 2 },
                { id: 'cb_ba_state', fieldName: 'billingState', fieldLabel: 'State', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 3 },
                { id: 'cb_ba_zip', fieldName: 'billingPostalCode', fieldLabel: 'Zip Code', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 4 },
                { id: 'cb_ba_country', fieldName: 'billingCountry', fieldLabel: 'Country', fieldType: 'text', required: false, readonly: false, width: 'full', column: 1, order: 5 },
                { id: 'cb_ba_sequences', fieldName: 'sequences', fieldLabel: 'Sequences', fieldType: 'text', required: false, readonly: false, width: 'full', column: 2, order: 6 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
          {
            id: 'cb_service_mgmt',
            type: 'form',
            name: 'service_management',
            label: 'Sequences / Service Management',
            order: 3,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cb_sm_self', fieldName: 'managingFirm', fieldLabel: 'Self', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 1 },
                { id: 'cb_sm_acctmgrco', fieldName: 'accountManagerCompany', fieldLabel: 'Account Manager Company', fieldType: 'lookup', lookupObject: 'Account', required: false, readonly: true, width: 'full', column: 2, order: 2 },
                { id: 'cb_sm_acctmgr', fieldName: 'accountManager', fieldLabel: 'Account Manager', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 3 },
                { id: 'cb_sm_daytodaycontact', fieldName: 'dayToDayContact', fieldLabel: 'Day-to-Day Contact', fieldType: 'text', required: false, readonly: true, width: 'full', column: 2, order: 4 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
          {
            id: 'cb_system_info',
            type: 'form',
            name: 'system_information',
            label: 'System Information',
            order: 4,
            collapsible: true,
            collapsed: false,
            columns: 2,
            description: '',
            config: {
              fields: [
                { id: 'cb_si_createdby', fieldName: 'recordCreatedBy', fieldLabel: 'Created By', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 1 },
                { id: 'cb_si_modifiedby', fieldName: 'lastModifiedBy', fieldLabel: 'Last Modified By', fieldType: 'text', required: false, readonly: true, width: 'full', column: 2, order: 2 },
                { id: 'cb_si_createddate', fieldName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', required: false, readonly: true, width: 'full', column: 1, order: 3 },
                { id: 'cb_si_modifieddate', fieldName: 'lastModifiedDate', fieldLabel: 'Last Modified Date', fieldType: 'date', required: false, readonly: true, width: 'full', column: 2, order: 4 },
                { id: 'cb_si_legacyid', fieldName: 'legacyId', fieldLabel: 'LegacyId', fieldType: 'text', required: false, readonly: true, width: 'full', column: 1, order: 5 },
              ],
              layout: 'horizontal',
              showLabels: true,
            } as FormSectionConfig,
          },
        ],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 2: Credit Block (Subscriptions + Histories related lists only — customTabContent)
      {
        id: 'cb_client_details_tab',
        pageId: 'credit_block_client_detail',
        label: 'Credit Block',
        icon: 'Shield',
        order: 2,
        isDefault: false,
        lazyLoad: false,
        sections: [],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 3: Subscription workspace (SF-style subscription + bureaus — customTabContent)
      {
        id: 'cb_subscription_workspace',
        pageId: 'credit_block_client_detail',
        label: 'Subscription',
        icon: 'Gift',
        order: 3,
        isDefault: false,
        lazyLoad: true,
        sections: [],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 4: Activity
      {
        id: 'activity',
        pageId: 'credit_block_client_detail',
        label: 'Activity',
        icon: 'Activity',
        order: 4,
        isDefault: false,
        lazyLoad: true,
        sections: [
          {
            id: 'cb_activity_timeline',
            type: 'activity',
            name: 'activity_timeline',
            label: 'Activity',
            order: 1,
            collapsible: false,
            collapsed: false,
            columns: 1,
            description: 'Log calls, tasks, meetings, and notes for this client.',
            config: { recordType: 'client' } as ActivitySectionConfig,
          },
        ],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 5: Notes
      {
        id: 'notes',
        pageId: 'credit_block_client_detail',
        label: 'Notes',
        icon: 'StickyNote',
        order: 5,
        isDefault: false,
        lazyLoad: false,
        sections: [],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 6: Files
      {
        id: 'files',
        pageId: 'credit_block_client_detail',
        label: 'Files',
        icon: 'Paperclip',
        order: 6,
        isDefault: false,
        lazyLoad: true,
        sections: [],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
      // Tab 7: Credit Report
      {
        id: 'credit-report',
        pageId: 'credit_block_client_detail',
        label: 'Credit Report',
        icon: 'FileText',
        order: 7,
        isDefault: false,
        lazyLoad: true,
        sections: [],
        visibility: { roles: ['admin', 'user', 'manager'] },
        permissions: { canView: ['admin', 'user', 'manager'], canEdit: ['admin', 'manager'], canDelete: ['admin'] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'system',
        updatedBy: 'system',
      },
    ],
  },
};

/**
 * CS Audit Record Interface
 */
export interface CSAudit {
  id: string;
  auditId: string;
  auditType: string;
  auditor: string;
  dueDate: string;
  phaseStatus: string;
}

/**
 * Mock CS Audits data
 * Sample audit records for testing the CS Audits table
 */
const mockCSAudits: CSAudit[] = [
  {
    id: '1',
    auditId: 'CyberAudit-1001',
    auditType: 'Quarter Audit',
    auditor: 'Sarah Johnson',
    dueDate: '2025-09-25',
    phaseStatus: 'P1 - In Progress'
  },
  {
    id: '2',
    auditId: 'CyberAudit-1002',
    auditType: 'Semi-Annual Audit',
    auditor: 'Michael Chen',
    dueDate: '2025-10-15',
    phaseStatus: 'P2 - Review'
  },
  {
    id: '3',
    auditId: 'CyberAudit-1003',
    auditType: 'Annual Compliance Audit',
    auditor: 'Emily Rodriguez',
    dueDate: '2025-11-30',
    phaseStatus: 'P1 - Planning'
  }
];

// ============================================
// RECORD PAGE → APP CONFIG MAPPER
// ============================================

/**
 * Normalizes an application name to the pageId prefix convention.
 * "CyberSweep" → "cybersweep", "Credit Block" → "credit_block"
 */
function normalizeAppName(app?: string): string | null {
  if (!app) return null;
  return app.toLowerCase().replace(/\s+/g, '_');
}

/** Map a RecordPage component type to a TabSection type */
function mapComponentType(type: string): TabSection['type'] {
  switch (type) {
    case 'field-section': return 'form';
    case 'related-list': return 'related-list';
    case 'activity-timeline': return 'activity';
    default: return 'widget';
  }
}

/** Build a TabSection config object appropriate for the section type */
function buildSectionConfig(
  comp: PageComponent,
  sectionType: TabSection['type']
): TabSection['config'] {
  if (sectionType === 'form') {
    return { fields: comp.config?.fields ?? [], showLabels: true } as FormSectionConfig;
  }
  if (sectionType === 'widget') {
    return { componentName: comp.type, props: comp.config ?? {} } as WidgetSectionConfig;
  }
  // activity / related-list — return minimal valid shape
  return { componentName: comp.type, props: comp.config ?? {} } as WidgetSectionConfig;
}

/** Map an Object Manager RecordPage to the ApplicationPageConfig shape */
function mapRecordPageToAppConfig(page: RecordPage, pageId: string): ApplicationPageConfig {
  const tabs: TabConfiguration[] = page.tabs.map((tab: PageTab) => {
    const sections: TabSection[] = tab.sections.flatMap((section: PageSection) =>
      section.components.map((comp: PageComponent) => {
        const sectionType = mapComponentType(comp.type);
        return {
          id: `${section.id}_${comp.id}`,
          type: sectionType,
          name: comp.id,
          label: comp.title || section.name,
          order: section.order * 100 + comp.order,
          collapsible: false,
          collapsed: false,
          columns: section.columns,
          config: buildSectionConfig(comp, sectionType),
        } as TabSection;
      })
    );

    return {
      id: tab.id,
      pageId,
      label: tab.label,
      icon: undefined,
      order: tab.order,
      isDefault: tab.isDefault,
      sections,
    } as TabConfiguration;
  });

  return {
    id: pageId,
    applicationName: normalizeAppName(page.application) ?? 'global',
    pageName: page.name,
    pageType: 'detail',
    title: page.name,
    description: page.description,
    tabs,
  };
}

/**
 * pageId → application name mapping.
 * e.g. "cybersweep_client_detail" → "cybersweep"
 */
const PAGE_ID_TO_APP: Record<string, string> = {
  cybersweep_client_detail: 'cybersweep',
  credit_block_client_detail: 'credit_block',
  credit_repair_client_detail: 'credit_repair',
};

/** Map pageId to the base object name so we can derive the Gobodo objectTypeKey dynamically */
const PAGE_ID_TO_OBJECT_BASE: Record<string, string> = {
  cybersweep_client_detail: 'Client',
  credit_block_client_detail: 'Client',
  credit_repair_client_detail: 'Client',
};

/** Strip Gobodo env suffix for object name comparison (e.g. Client__stg3_c → client) */
function normalizeObjectName(name: string): string {
  return name.replace(/(__stg3_c|__stg_c|__c)$/i, '').toLowerCase();
}

function normalizeFieldBaseName(name: string): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/(__stg3_c|__stg_c|__dev_c|__c)$/i, '');
}

function normalizeFieldLabel(label: string): string {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Look up a saved record page (from Object Manager) that matches this pageId.
 * Only returns a config if the page has real content — at least one field-section
 * component with fields configured, or a non-field-section component.
 */
function findSavedPageConfig(pageId: string): ApplicationPageConfig | null {
  if (!pageId) return null;
  const appKey = PAGE_ID_TO_APP[pageId];
  if (!appKey) return null;

  const pages = persistedOMRecordPages.get();
  const CLIENT_BASE = 'client';
  const match = pages.find(p =>
    p.isActive &&
    normalizeAppName(p.application) === appKey &&
    (normalizeObjectName(p.objectName) === CLIENT_BASE || p.objectName === 'obj_client')
  );

  if (!match) return null;

  // Only use saved config if admin has actually configured field content.
  // An empty field-section (fields: []) is treated as a placeholder, not real content.
  const hasContent = match.tabs.length > 0 &&
    match.tabs.some(t =>
      t.sections.length > 0 &&
      t.sections.some(s =>
        s.components.some(c =>
          c.type !== 'field-section' ||
          (Array.isArray(c.config?.fields) && c.config.fields.length > 0)
        )
      )
    );

  return hasContent ? mapRecordPageToAppConfig(match, pageId) : null;
}

// ============================================
// GOBODO PAGE LAYOUT → APP CONFIG CONVERTER
// ============================================

/**
 * Fetch page configuration from Gobodo Page Layout Templates.
 * Maps pageId → application name, then queries Gobodo for matching
 * PageLayoutTemplate records with their sections and fields.
 * Converts the Gobodo hierarchy (template → sections → fields)
 * to the ApplicationPageConfig shape (tabs → sections → fields).
 */
async function fetchGobodoPageConfig(pageId: string): Promise<ApplicationPageConfig | null> {
  if (!pageId) return null;
  const appKey = PAGE_ID_TO_APP[pageId];
  if (!appKey) return null;

  try {
    // Derive objectTypeKey dynamically from pageId instead of hardcoding
    const objectBase = PAGE_ID_TO_OBJECT_BASE[pageId];
    const objectTypeKey = objectBase ? normalizeToGobodoFormat(objectBase) : undefined;

    console.log(`🔍 fetchGobodoPageConfig: pageId=${pageId}, app=${appKey}, objectTypeKey=${objectTypeKey}`);

    let templates: any[] = [];

    // Try with objectTypeKey first (more specific)
    if (objectTypeKey) {
      const { data } = await fetchPageLayoutTemplates({
        application: appKey as any,
        objectTypeKey,
      });
      if (data && data.length > 0) templates = data;
    }

    // Fallback: try without objectTypeKey filter
    if (templates.length === 0) {
      const { data: allAppTemplates } = await fetchPageLayoutTemplates({
        application: appKey as any,
      });
      if (allAppTemplates && allAppTemplates.length > 0) {
        templates = allAppTemplates;
      }
    }

    if (templates.length === 0) {
      console.log(`  No Gobodo templates found for ${appKey}`);
      return null;
    }

    // Pick the best template: prefer default, then most sections, then first
    const template = templates.find((t: any) => t.isDefault)
      || templates.reduce((best: any, t: any) =>
        (t.sections?.length || 0) > (best.sections?.length || 0) ? t : best, templates[0]);

    if (!template.sections || template.sections.length === 0) {
      console.log(`  Template "${template.name}" has no sections`);
      return null;
    }

    console.log(`  Using template "${template.name}" with ${template.sections.length} sections`);

    // Group sections by their `tab` property.
    // LayoutBuilder doesn't set `tab`, so derive it from section name or use 'details'.
    const tabMap = new Map<string, typeof template.sections>();
    for (const section of template.sections) {
      // Use section.tab if set, otherwise derive from section name, fallback to 'details'
      const tabKey = (section as any).tab
        || deriveTabKey(section.name)
        || 'details';
      if (!tabMap.has(tabKey)) tabMap.set(tabKey, []);
      tabMap.get(tabKey)!.push(section);
    }

    // Convert tab groups to TabConfiguration[]
    let tabOrder = 1;
    const tabs: TabConfiguration[] = [];
    tabMap.forEach((sections, tabKey) => {
      // Collapse duplicate section rows per tab (keep the one with more fields).
      const sectionByName = new Map<string, any>();
      for (const section of sections) {
        const key = String(section.name || section.label || '')
          .trim()
          .toLowerCase();
        if (!key) continue;
        const existing = sectionByName.get(key);
        if (!existing || (section.fields?.length || 0) > (existing.fields?.length || 0)) {
          sectionByName.set(key, section);
        }
      }

      const tabSections: TabSection[] = Array.from(sectionByName.values()).map((section: any) => ({
        id: section.id || `section_${tabKey}_${section.order || 0}`,
        type: 'form' as const,
        name: (section.name || 'section').toLowerCase().replace(/\s+/g, '_'),
        label: section.name || section.label || 'Section',
        order: section.order || 0,
        collapsible: section.collapsible ?? true,
        collapsed: false,
        columns: (section.columns as 1 | 2 | 3 | 4) || 2,
        description: section.description,
        config: {
          fields: (section.fields || []).map((f: any, idx: number) => {
            // Gobodo fields come with various property names depending on the layer:
            // gobodoToField: fieldKey, label, type, fieldId
            // Raw Gobodo props: FieldKey__c, Label__c, Type__c, FieldId__c
            // LayoutBuilder sync: fieldName, fieldLabel
            const fieldName = f.fieldKey || f.fieldName || f.fieldApiName || f.fieldId || '';
            const fieldLabel = f.label || f.fieldLabel
              || fieldName.replace(/__c$/i, '').replace(/__\w+_c$/i, '').replace(/_/g, ' ');
            const rawType = f.type || f.fieldType || 'text';
            // Gobodo 'relationship'-typed fields render correctly only when treated as lookup —
            // map to 'lookup' and surface relTypeId / lookupObject so the search picker can query the related type.
            const isRelationshipField = String(rawType).toLowerCase() === 'relationship';
            const lookupObject = f.lookupObject || f.relatedObjectType || f.endObjectTypeKey;
            const relTypeId = f.relTypeId ?? f.relationshipTypeId;
            return {
              id: `field_${fieldName}_${idx}`,
              fieldName,
              fieldLabel,
              fieldType: isRelationshipField ? 'lookup' : rawType,
              required: f.required || false,
              readonly: f.readOnly || false,
              width: 'full',
              column: f.column || ((idx % 2) + 1),
              order: f.order ?? idx,
              ...(lookupObject ? { lookupObject } : {}),
              ...(relTypeId ? { relTypeId } : {}),
            };
          }).filter((f: any) => f.fieldName)
            .filter((field: any, idx: number, arr: any[]) => {
              const fieldBase = normalizeFieldBaseName(field.fieldName);
              const labelKey = normalizeFieldLabel(field.fieldLabel);
              return arr.findIndex((x: any) => {
                const xBase = normalizeFieldBaseName(x.fieldName);
                const xLabel = normalizeFieldLabel(x.fieldLabel);
                return xBase === fieldBase || (labelKey && xLabel === labelKey);
              }) === idx;
            }),
          layout: 'horizontal',
          showLabels: true,
        } as FormSectionConfig,
      })).sort((a, b) => (a.order || 0) - (b.order || 0));

      const tabLabel = tabKey.charAt(0).toUpperCase() + tabKey.slice(1).replace(/[-_]/g, ' ');
      tabs.push({
        id: tabKey,
        pageId,
        label: tabLabel,
        icon: undefined,
        order: tabOrder++,
        isDefault: tabOrder === 2, // First tab is default
        lazyLoad: tabOrder > 2,
        sections: tabSections,
      } as TabConfiguration);
    });

    console.log(`✅ Page configuration loaded from Gobodo: ${pageId} (${template.name}, ${tabs.length} tabs, ${template.sections.length} sections)`);
    return {
      id: pageId,
      applicationName: appKey,
      pageName: template.name,
      pageType: 'detail',
      title: template.name,
      description: template.description,
      tabs,
    };
  } catch (error) {
    console.warn(`⚠️ Failed to load Gobodo page config for ${pageId}:`, error);
    return null;
  }
}

/**
 * Derive a tab key from section name.
 * Maps common section name patterns to tab groups.
 */
function deriveTabKey(sectionName: string): string {
  if (!sectionName) return 'details';
  const lower = sectionName.toLowerCase();
  if (lower.includes('billing')) return 'billing';
  if (lower.includes('activity') || lower.includes('history')) return 'activity';
  if (lower.includes('document') || lower.includes('file')) return 'documents';
  if (lower.includes('note')) return 'notes';
  if (lower.includes('audit')) return 'audit';
  if (lower.includes('config') || lower.includes('setting') || lower.includes('pii')) return 'configuration';
  return 'details';
}

// ============================================
// PAGE CONFIGURATION RESOLVER
// ============================================

/**
 * Fetches page configuration using a priority chain:
 * 1. Gobodo Page Layout Templates (when USE_GOBODO_API=true) — the source of truth
 * 2. localStorage Record Pages (legacy fallback)
 * 3. Hardcoded mockApplicationConfigs (development fallback)
 */
const fetchPageConfiguration = async (pageId: string): Promise<ApplicationPageConfig> => {
  console.log(`🔍 Loading page configuration: ${pageId}`);

  // 1. Gobodo is the source of truth in production
  if (USE_GOBODO_API) {
    const gobodoConfig = await fetchGobodoPageConfig(pageId);
    if (gobodoConfig) {
      // If we have a hardcoded config with tabs, prefer it over Gobodo
      // (we control tab structure for Credit Block + CyberSweep)
      const hardcoded = mockApplicationConfigs[pageId];
      if (hardcoded?.tabs?.length) {
        console.log(`📎 Using hardcoded tab config for ${pageId} (${hardcoded.tabs.length} tabs, Gobodo config available but overridden)`);
        return hardcoded;
      }
      return gobodoConfig;
    }
    // If Gobodo returned nothing, fall straight to hardcoded — skip localStorage
    // to avoid stale Record Pages overriding Gobodo's authority
    console.log(`⚠️ No Gobodo config for ${pageId}, falling back to hardcoded`);
  } else {
    // 2. Dev-only: try localStorage Record Pages when Gobodo is disabled
    if (!pageId.startsWith('credit_block')) {
      const savedConfig = findSavedPageConfig(pageId);
      if (savedConfig) {
        console.log(`✅ Page configuration loaded from localStorage: ${pageId}`);
        return savedConfig;
      }
    }
  }

  // 3. Always-available hardcoded fallback
  const config = mockApplicationConfigs[pageId];

  if (!config) {
    throw new Error(`Page configuration not found: ${pageId}`);
  }

  console.log(`✅ Page configuration loaded (mock fallback): ${pageId}`, config);
  return config;
};

/**
 * Simulates fetching a specific tab configuration
 * Includes 300ms delay to simulate network latency
 */
const fetchTabConfiguration = async (pageId: string, tabId: string): Promise<TabConfiguration> => {
  console.log(`🔍 Loading tab configuration: ${pageId} → ${tabId}`);

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 300));

  const pageConfig = mockApplicationConfigs[pageId];

  if (!pageConfig) {
    throw new Error(`Page configuration not found: ${pageId}`);
  }

  const tabConfig = pageConfig.tabs.find(tab => tab.id === tabId);

  if (!tabConfig) {
    throw new Error(`Tab configuration not found: ${pageId} → ${tabId}`);
  }

  console.log(`✅ Tab configuration loaded: ${tabId}`, tabConfig);
  return tabConfig;
};

/**
 * Simulates updating a page configuration
 * Includes 500ms delay to simulate network latency
 */
const updatePageConfiguration = async (
  pageId: string,
  updates: Partial<ApplicationPageConfig>
): Promise<ApplicationPageConfig> => {
  console.log(`💾 Updating page configuration: ${pageId}`, updates);

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const existingConfig = mockApplicationConfigs[pageId];

  if (!existingConfig) {
    throw new Error(`Page configuration not found: ${pageId}`);
  }

  // Merge updates
  const updatedConfig = {
    ...existingConfig,
    ...updates,
    updatedAt: new Date().toISOString(),
    updatedBy: 'current_user' // Would come from auth context in real app
  };

  // Update mock storage
  mockApplicationConfigs[pageId] = updatedConfig;

  console.log(`✅ Page configuration updated: ${pageId}`, updatedConfig);
  return updatedConfig;
};

const CSAUDIT_OBJECT = 'CSAudit__c';

function mapGobodoToCSAudit(r: Record<string, unknown>): CSAudit {
  const id = String(r.id ?? r.key ?? r.Id ?? '');
  return {
    id,
    auditId: String(r.id ?? r.key ?? r.AuditId__c ?? r.auditId ?? id),
    auditType: String(r.AuditType__c ?? r.auditType ?? ''),
    auditor: String(r.Auditor__c ?? r.auditor ?? ''),
    dueDate: String(r.DueDate__c ?? r.dueDate ?? ''),
    phaseStatus: String(r.PhaseStatus__c ?? r.phaseStatus ?? ''),
  };
}

/**
 * Fetches CS Audits from Gobodo when USE_GOBODO_API.
 * Uses Traversal API (Client → CSAudit) when recordId provided; else fetches all.
 *
 * @param recordId - Optional client/record ID to filter audits (uses traversal when set)
 */
const fetchCSAudits = async (recordId?: string): Promise<CSAudit[]> => {
  if (USE_GOBODO_API) {
    if (recordId) {
      const traversalRecords = await fetchClientCSAuditsViaTraversal(recordId);
      const hydrated = await hydrateCSAuditRecordsForListUi(traversalRecords as Record<string, unknown>[]);
      return hydrated.map(mapGobodoToCSAudit);
    }

    const records = await getRecordsForObject(CSAUDIT_OBJECT);
    return (records as Record<string, unknown>[]).map(mapGobodoToCSAudit);
  }

  await new Promise(resolve => setTimeout(resolve, 300));
  return mockCSAudits;
};

// ============================================
// REACT QUERY HOOKS
// ============================================

/**
 * Hook to fetch application page configuration
 *
 * @param pageId - The page identifier
 * @param options - React Query options
 * @returns Query result with page configuration data
 *
 * @example
 * ```tsx
 * const { data: pageConfig, isLoading, error } = useApplicationPageConfig('cybersweep_client_detail');
 * ```
 */
export const useApplicationPageConfig = (
  pageId: string,
  options?: {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
  }
) => {
  const isGobodoMode = USE_GOBODO_API;
  return useQuery({
    queryKey: configKeys.page(pageId),
    queryFn: () => fetchPageConfiguration(pageId),
    enabled: options?.enabled ?? true,
    // In Gobodo mode, prefer fresh config over cache to keep UI layout in sync
    // right after Visual Builder saves and route transitions.
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? isGobodoMode,
    refetchOnMount: isGobodoMode ? 'always' : true,
    refetchOnReconnect: isGobodoMode ? 'always' : true,
    staleTime: isGobodoMode ? 0 : 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

/**
 * Hook to fetch a specific tab configuration
 *
 * @param pageId - The page identifier
 * @param tabId - The tab identifier
 * @param options - React Query options
 * @returns Query result with tab configuration data
 *
 * @example
 * ```tsx
 * const { data: tabConfig, isLoading } = useTabConfiguration(
 *   'cybersweep_client_detail',
 *   'client_details_tab'
 * );
 * ```
 */
export const useTabConfiguration = (
  pageId: string,
  tabId: string,
  options?: {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
  }
) => {
  return useQuery({
    queryKey: configKeys.tab(pageId, tabId),
    queryFn: () => fetchTabConfiguration(pageId, tabId),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to update page configuration
 *
 * @returns Mutation object for updating page configuration
 *
 * @example
 * ```tsx
 * const updateConfig = useUpdatePageConfig();
 *
 * updateConfig.mutate({
 *   pageId: 'cybersweep_client_detail',
 *   updates: { title: 'New Title' }
 * });
 * ```
 */
export const useUpdatePageConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pageId, updates }: {
      pageId: string;
      updates: Partial<ApplicationPageConfig>;
    }) => updatePageConfiguration(pageId, updates),

    onSuccess: (data, variables) => {
      // Invalidate and refetch page config
      queryClient.invalidateQueries({
        queryKey: configKeys.page(variables.pageId)
      });

      // Also invalidate tab queries for this page
      queryClient.invalidateQueries({
        queryKey: configKeys.tabs(variables.pageId)
      });

      console.log(`🔄 Cache invalidated for page: ${variables.pageId}`);
    },

    onError: (error, variables) => {
      console.error(`❌ Failed to update page configuration: ${variables.pageId}`, error);
    }
  });
};

/**
 * Hook to fetch CS Audits
 *
 * @param recordId - Optional client/record ID to filter audits
 * @param options - React Query options
 * @returns Query result with CS Audits data
 *
 * @example
 * ```tsx
 * const { data: audits, isLoading, error } = useCSAudits('CS-2024-001');
 * ```
 */
export const useCSAudits = (
  recordId?: string,
  options?: {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
  }
) => {
  return useQuery({
    queryKey: ['cs_audits', recordId],
    queryFn: () => fetchCSAudits(recordId),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Prefetch page configuration
 * Useful for optimistic loading
 *
 * @param queryClient - React Query client instance
 * @param pageId - The page identifier
 */
export const prefetchPageConfig = async (
  queryClient: QueryClient,
  pageId: string
) => {
  await queryClient.prefetchQuery({
    queryKey: configKeys.page(pageId),
    queryFn: () => fetchPageConfiguration(pageId),
    staleTime: USE_GOBODO_API ? 0 : 5 * 60 * 1000,
  });

  console.log(`⚡ Prefetched page configuration: ${pageId}`);
};

/**
 * Get page configuration from cache
 * Returns undefined if not in cache
 *
 * @param queryClient - React Query client instance
 * @param pageId - The page identifier
 */
export const getPageConfigFromCache = (
  queryClient: QueryClient,
  pageId: string
): ApplicationPageConfig | undefined => {
  return queryClient.getQueryData(configKeys.page(pageId));
};

/**
 * Set page configuration in cache
 * Useful for optimistic updates
 *
 * @param queryClient - React Query client instance
 * @param pageId - The page identifier
 * @param data - The page configuration data
 */
export const setPageConfigInCache = (
  queryClient: QueryClient,
  pageId: string,
  data: ApplicationPageConfig
) => {
  queryClient.setQueryData(configKeys.page(pageId), data);
  console.log(`💾 Set page configuration in cache: ${pageId}`);
};

// ============================================
// EXPORTS
// ============================================

/**
 * Export mock data for testing purposes
 */
export const __mockData = {
  configs: mockApplicationConfigs,
  csAudits: mockCSAudits,
};
